import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Node } from 'reactflow';
import { FiDollarSign, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const FABRIC_SKU_COSTS: Record<string, number> = {
  Trial: 0,
  F2: 262, F4: 524, F8: 1048, F16: 2096,
  F32: 4192, F64: 8384, F128: 16768,
};

interface FinOpsPanelProps {
  nodes: Node[];
  onClose: () => void;
}

const FinOpsPanel: React.FC<FinOpsPanelProps> = ({ nodes, onClose }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const saved = localStorage.getItem('finopsPanel.position');
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 380, y: 80 };
  });
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);

  useEffect(() => {
    localStorage.setItem('finopsPanel.position', JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging || !dragRef.current) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 360, dragRef.current.posX + (e.clientX - dragRef.current.startX))),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.posY + (e.clientY - dragRef.current.startY))),
      });
    };
    const onUp = () => { setIsDragging(false); dragRef.current = null; };
    if (isDragging) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [isDragging]);

  const stats = useMemo(() => {
    const capacities = nodes.filter(n => (n.data as any)?.type === 'fabric_capacity');
    const items = capacities.map(cap => {
      const sku: string = (cap.data as any)?.params?.sku || 'F2';
      const cost = FABRIC_SKU_COSTS[sku] ?? 0;
      const workspaces = nodes.filter(
        n => {
          if ((n.data as any)?.type !== 'fabric_workspace') return false;
          if (n.parentNode === cap.id) return true;
          // workspace inside a domain that is inside this capacity
          const domainParent = nodes.find(d => d.id === n.parentNode && (d.data as any)?.type === 'fabric_domain');
          return domainParent?.parentNode === cap.id;
        }
      );
      const otherItems = nodes.filter(
        n => n.parentNode === cap.id && (n.data as any)?.type !== 'fabric_workspace' && (n.data as any)?.type !== 'fabric_domain'
      );
      const sharePerWs = workspaces.length > 0 ? Math.round(cost / workspaces.length) : cost;
      return { cap, sku, cost, workspaces, otherItems, sharePerWs };
    });

    const capacityIds = new Set(capacities.map(c => c.id));
    const domainIds = new Set(nodes.filter(n => (n.data as any)?.type === 'fabric_domain' && capacityIds.has(n.parentNode || '')).map(n => n.id));
    const orphanWorkspaces = nodes.filter(
      n => (n.data as any)?.type === 'fabric_workspace' &&
           !capacityIds.has(n.parentNode || '') &&
           !domainIds.has(n.parentNode || '')
    );

    const total = items.reduce((s, i) => s + i.cost, 0);
    return { items, orphanWorkspaces, total };
  }, [nodes]);

  const hasFabric = stats.items.length > 0 || stats.orphanWorkspaces.length > 0;

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: 340,
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: 10,
        boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
        zIndex: 1200,
        userSelect: isDragging ? 'none' : 'auto',
        overflow: 'hidden',
      }}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('.fp-drag')) {
          setIsDragging(true);
          dragRef.current = { startX: e.clientX, startY: e.clientY, posX: position.x, posY: position.y };
          e.preventDefault();
        }
      }}
    >
      {/* Header */}
      <div
        className="fp-drag"
        style={{
          background: 'linear-gradient(135deg, #038387 0%, #005F61 100%)',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'grab',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiDollarSign size={16} color="white" />
          <span style={{ color: 'white', fontWeight: 700, fontSize: 13 }}>FinOps — Estimations de coût</span>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', display: 'flex', padding: 2 }}
        >
          <FiX size={16} />
        </button>
      </div>

      <div style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', padding: 12 }}>
        {!hasFabric && (
          <div style={{ textAlign: 'center', padding: '24px 16px', color: '#9CA3AF', fontSize: 12 }}>
            <FiDollarSign size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>Aucun nœud <strong>fabric_capacity</strong> détecté.</div>
            <div style={{ marginTop: 4 }}>Glissez une Fabric Capacity sur le canvas pour estimer les coûts.</div>
          </div>
        )}

        {stats.items.map(({ cap, sku, cost, workspaces, otherItems, sharePerWs }) => {
          const name: string = (cap.data as any)?.label || (cap.data as any)?.params?.name || cap.id;
          const isOpen = !collapsed[cap.id];
          return (
            <div key={cap.id} style={{ marginBottom: 10, border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
              {/* Capacity header row */}
              <div
                onClick={() => setCollapsed(prev => ({ ...prev, [cap.id]: !prev[cap.id] }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: '#F0FDFE', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isOpen ? <FiChevronDown size={13} color="#038387" /> : <FiChevronRight size={13} color="#038387" />}
                  <img src="/icons/fabric/fabric_capacity.svg" alt="" width={18} height={18} style={{ objectFit: 'contain' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0F5256' }}>{name}</span>
                  <span style={{
                    fontSize: 10, background: '#038387', color: 'white',
                    padding: '1px 6px', borderRadius: 10, fontWeight: 600,
                  }}>{sku}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#038387' }}>
                  ~€{cost.toLocaleString('fr-FR')}/mois
                </span>
              </div>

              {/* Workspaces breakdown */}
              {isOpen && (
                <div style={{ padding: '6px 12px 10px' }}>
                  {workspaces.length === 0 && (
                    <div style={{ fontSize: 11, color: '#9CA3AF', paddingTop: 4 }}>Aucun workspace rattaché</div>
                  )}
                  {workspaces.map(ws => {
                    const wsName: string = (ws.data as any)?.label || (ws.data as any)?.params?.name || ws.id;
                    return (
                      <div key={ws.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 0', borderBottom: '1px solid #F3F4F6', fontSize: 11,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#374151' }}>
                          <img src="/icons/fabric/group_workspace_64_non-item.svg" alt="" width={14} height={14} style={{ objectFit: 'contain' }} />
                          {wsName}
                        </div>
                        <span style={{ color: '#6B7280' }}>~€{sharePerWs.toLocaleString('fr-FR')}/mois</span>
                      </div>
                    );
                  })}

                  {workspaces.length > 0 && (
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 6 }}>
                      Coût partagé entre {workspaces.length} workspace{workspaces.length > 1 ? 's' : ''} · {otherItems.length} autre{otherItems.length > 1 ? 's' : ''} item{otherItems.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {stats.orphanWorkspaces.length > 0 && (
          <div style={{ padding: '6px 10px', background: '#FEF3C7', borderRadius: 6, fontSize: 11, color: '#92400E', marginBottom: 8 }}>
            ⚠ {stats.orphanWorkspaces.length} workspace{stats.orphanWorkspaces.length > 1 ? 's' : ''} sans capacity — coût non estimable
          </div>
        )}

        {/* Total */}
        {stats.total > 0 && (
          <div style={{
            marginTop: 4, padding: '10px 14px',
            background: 'linear-gradient(135deg, #F0FDFE 0%, #E6F7F8 100%)',
            border: '1px solid #038387', borderRadius: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0F5256' }}>Total mensuel estimé</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#038387' }}>
                ~€{stats.total.toLocaleString('fr-FR')}/mois
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
              ~€{(stats.total * 12).toLocaleString('fr-FR')}/an · Pay-as-you-go · hors remises EA
            </div>
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>
              {stats.items.length} capacit{stats.items.length > 1 ? 'és' : 'é'} Fabric · région West Europe
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinOpsPanel;
